import type { ApiErrorInfo } from "@/api/errorStatus";
import type { Rule } from "@/logic-engine";
import type { EditorScreen } from "./types/screen";
import type { EditorSettings } from "./types/settings";
import { DEFAULT_FUNNEL_LANGUAGE } from "@/utils/defaultFunnelLanguage";

// WEB-2552 (пачка 49, заход Р0): состояние редактора (EditorState,
// ContentSnapshot, AiChatEntry, initialState) вынесено из editorSlice.ts
// («перенос токен в токен»).

/**
 * A single AI chat history entry. Fully serializable — no Date objects.
 * Timestamp is a Unix epoch number (Date.now()).
 */
export interface AiChatEntry {
  instruction: string;
  summary: string;
  operationsApplied: number;
  timestamp: number;
}

/**
 * WEB-1753: четыре вида, в которых блок «Оффер удержания» (`downsell_offer`)
 * живёт в опубликованном портале. Автор настраивает их все (поле «Текст после
 * успеха» — про `accepted`), а видел на канвасе только `offer`. Значения
 * названы по ветвям самого блока, чтобы переключатель редактора и живой рендер
 * не разъехались формулировками:
 *  - `offer`     — покупатель ещё не принял решение (карточка с кнопкой);
 *  - `accepted`  — предложение принято (текст успеха);
 *  - `no_access` — доступа нет (приглашение войти по почте);
 *  - `empty`     — предлагать нечего (покупателю блок не печатается).
 */
export type DownsellOfferPreviewState =
  | "offer"
  | "accepted"
  | "no_access"
  | "empty";

export interface EditorState {
  editorType: "quiz" | "paywall" | null;
  resourceId: string | null;
  resourceSlug: string | null;
  projectId: string | null;
  /**
   * WEB-3001: генерация, от которой создан открытый квиз или пейволл (колонка
   * `generation_id`, WEB-2169). По ней чат ИИ-редактора читает список «ИИ не
   * знал эти числа и отзывы — проверьте N мест». Пишется на КАЖДОЙ загрузке
   * (null у ресурса без генерации) — иначе чужой список «залипал» бы.
   * Необязательное — тест-фикстуры состояния без поля компилируются.
   */
  generationId?: string | null;
  /**
   * WEB-3872 (правовое правило WEB-2015): префиксы адресов нашего хранилища
   * (`https://<хост>/img/`) из ответа открытия воронки — рядом с настройками,
   * отдельного запроса нет. Файл своего шрифта с адресом не из этих префиксов
   * редактор не подключает и в выбор шрифта не выдаёт. Пишется на КАЖДОЙ
   * загрузке (поля нет — пустой список, закрыто); ответ сохранения не трогает.
   * Необязательное — тест-фикстуры состояния без поля компилируются.
   */
  ownAssetUrlPrefixes?: string[];
  name: string;
  content: EditorScreen[];
  settings: EditorSettings;
  selectedScreenId: string | null;
  selectedBlockId: string | null;
  isDirty: boolean;
  /**
   * WEB-608: были ли РУЧНЫЕ правки конструктора после последнего применённого
   * AI-хода. В отличие от isDirty («не сохранено прямо сейчас», гасится
   * автосейвом за 1-2 сек), этот флаг держится, пока правки не поглощены новым
   * ходом (markTurnApplied) или перезагрузкой с сервера (load fulfilled /
   * revert). По нему гейтится confirm перед revert-ом — иначе автосейв
   * «съедал» сигнал и ручные правки терялись молча. Optional — pre-existing
   * тест-фикстуры без поля продолжают компилиться (undefined = false).
   */
  manualEditsSinceTurn?: boolean;
  isSaving: boolean;
  isLoading: boolean;
  /**
   * Ревизия ранней части пачки 49 (з6): номер ПОСЛЕДНЕГО запроса открытия
   * (ставит `load*ForEditor.pending`). Ответ и отказ открытия принимаются
   * только с этим номером — `isCurrentLoad` в `openResource.ts`. Пусто —
   * редактор сброшен и ничего не открывал. Optional — прежние тест-фикстуры
   * без поля.
   */
  loadRequestId?: string | null;
  /**
   * WEB-2561: сеанс открытия — номер запроса, начавшего открытие ЭТОГО
   * ресурса (перечитывание того же ресурса его не меняет, сброс стирает).
   * Ответ сохранения принимается только из того же сеанса —
   * `isSameWriteSession` в `openResource.ts`. Optional — прежние фикстуры.
   */
  openSession?: string;
  /** WEB-1955: разобранный отказ — фразу выбирает экран (localizeError). */
  error: ApiErrorInfo | null;
  /**
   * WEB-1908 (возврат приёмки В11-Л6): ПРИЧИНА последнего тарифного отказа
   * (`PAYMENT_REQUIRED_*`) — её показывает окно тарифа редактора строкой над
   * тарифами. Отдельным полем, а не в payload отказа, потому что отказ по
   * тарифу уходит ГОЛОЙ СТРОКОЙ-приметой `TIER_LIMIT_SENTINEL` (WEB-391): на
   * ней держатся сравнения `=== SENTINEL` в редакторе и в политике автосейва,
   * и менять её форму ради текста — менять работающий механизм ради надписи.
   * `null` — отказ был без причины (старый сервер); поле переписывается на
   * КАЖДОМ тарифном отказе, чтобы окно не показало причину прошлого раза.
   * Необязательное: тест-фикстуры состояния без поля продолжают компилиться.
   */
  tierLimitReasonCode?: string | null;
  /** AI chat history for the lifetime of the open editor session. */
  aiChatHistory: AiChatEntry[];
  /** Rule-based navigation/visibility logic (draftLogic on the entity). */
  logic: Rule[];
  /**
   * WEB-430: язык контента (source language перевода в конструкторе). Берётся из
   * quiz/paywall.language при загрузке; по умолчанию 'ru' (сервис русскоязычный).
   */
  language: string;
  /**
   * WEB-1333 (п.5): снимок серверного `updatedAt`, на котором основан текущий
   * черновик. Уходит в PATCH как `baseUpdatedAt` — при расхождении сервер
   * отвечает 409 STALE_DRAFT (вторая вкладка / второй участник), и правка не
   * затирает чужую. null = сверка выключена до следующей синхронизации
   * (после хода ИИ-чата: он пишет в базу сам, а своего updatedAt не
   * возвращает — см. invalidateDraftSnapshot). Optional — pre-existing
   * тест-фикстуры без поля продолжают компилиться.
   */
  serverUpdatedAt?: string | null;
  // WEB-347: appUrl/storeUrl removed from EditorState.
  // Source of truth = project.bridgeConfig.storeUrl (App Connection section).
  /**
   * WEB-624: undo/redo history stacks. Only the user-editable payload
   * (content + logic) is snapshotted — selection/ui/isDirty/isSaving are
   * intentionally excluded. Optional so pre-existing test fixtures that
   * construct an EditorState literal without these fields keep compiling;
   * `withHistory`/`undo`/`redo` default to `[]` when reading.
   */
  past?: ContentSnapshot[];
  future?: ContentSnapshot[];
  /**
   * WEB-683: transient UI flag — NOT persisted (not part of `content`, not
   * snapshotted for undo/redo). Controls whether the cookie_consent block's
   * inline preview is expanded in PhonePreview; toggled by a "Показать"/
   * "Скрыть" button in BlockPropertiesPanelExtended. Optional (like
   * past/future above) so pre-existing test fixtures that construct an
   * EditorState literal without it keep compiling; reducers/selectors treat
   * `undefined` as `false`.
   */
  cookiePreviewVisible?: boolean;
  /** QA WEB-673: транзиент показа downsell-попапа в превью (аналог cookie). */
  downsellPreviewVisible?: boolean;
  /**
   * WEB-1753: какой из четырёх видов блока `downsell_offer` показывать на
   * канвасе. Транзиент того же рода, что два флага выше: НЕ часть содержимого
   * воронки, не попадает в undo/redo и не уезжает на сервер. На поведение
   * опубликованного портала не влияет — там вид по-прежнему выбирается по
   * настоящим данным покупателя (см. DownsellOfferBlock).
   */
  downsellOfferPreviewState?: DownsellOfferPreviewState;
  /**
   * WEB-1505: открытый ресурс заблокирован понижением тарифа — он сверх лимита
   * нового тарифа и переведён в режим «только чтение»
   * (`backend/src/billing/services/downgrade-enforcement.service.ts`). Сервер
   * откажет ЛЮБОЙ записи по нему кодом 403 `RESOURCE_READONLY`
   * (`backend/src/shared/guards/assert-writable.ts`), поэтому конструктор
   * гасит сохранение и публикацию заранее — иначе человек правит текст,
   * жмёт «Сохранить» и теряет работу вместе с англоязычным отказом.
   *
   * Необязательное (как past/future/cookiePreviewVisible): тест-фикстуры и
   * состояния, собранные до появления поля, продолжают компилиться, а
   * `undefined` читается как «редактируемый».
   */
  isReadonly?: boolean;
  /**
   * WEB-2230: открытый ресурс ни разу не публиковался — сервер переводит его
   * с черновика, окно «Добавить перевод» предупреждает об этом до заказа.
   * Ставится на каждой загрузке (`neverPublishedOf`), снимается публикацией
   * открытого ресурса. Необязательное: `undefined` — «неизвестно»,
   * предупреждения нет.
   */
  neverPublished?: boolean;
}

/** WEB-624: snapshot of the undoable payload — content + logic only. */
export interface ContentSnapshot {
  content: EditorScreen[];
  logic: Rule[];
  /**
   * WEB-1239 (п.6 карточки): ОФОРМЛЕНИЕ в снимке отмены. Необязательное —
   * снимки, лежащие в стеке от предыдущей версии вкладки, его не несут, и
   * отмена на таком снимке оставляет текущее оформление как есть.
   */
  settings?: EditorSettings;
}

export const initialState: EditorState = {
  editorType: null,
  resourceId: null,
  resourceSlug: null,
  projectId: null,
  generationId: null,
  name: "",
  content: [],
  settings: {},
  selectedScreenId: null,
  selectedBlockId: null,
  isDirty: false,
  manualEditsSinceTurn: false,
  isSaving: false,
  isLoading: false,
  error: null,
  aiChatHistory: [],
  logic: [],
  /**
   * WEB-430 → WEB-1909: дефолт языка конструктора берётся из ОДНОЙ константы
   * умолчания языка воронки (решение владельца 05.09 — русский).
   *
   * Ревизия WEB-1907 (05.09), оговорка по месту. Это значение живёт ровно до
   * первого ответа сервера: колонка языка НЕ nullable, и загрузка ниже всегда
   * перезаписывает поле (`loadQuiz`/`loadPaywall`). То есть на умолчания
   * содержимого новых блоков (они читают `state.language`) оно влияет только
   * в редакторе БЕЗ загруженной воронки.
   *
   * Прежнее расхождение (сервисы ставили одно, умолчание колонки БД — другое)
   * сведено WEB-1909: сервисы, обе колонки, миграция 244 и это зеркало берут
   * значение из одного места, совпадение держит сторож двух концов.
   */
  language: DEFAULT_FUNNEL_LANGUAGE,
  // WEB-347: appUrl/storeUrl removed — source = project.bridgeConfig.storeUrl.
  // WEB-624: undo/redo history — empty on a fresh editor.
  past: [],
  future: [],
  // WEB-683: cookie_consent preview starts collapsed in the editor canvas.
  cookiePreviewVisible: false,
  downsellPreviewVisible: false,
  // WEB-1753: канвас открывается на том же виде, что автор видел до появления
  // переключателя — «предложение».
  downsellOfferPreviewState: "offer",
};
